// Phrasebook data + localStorage helpers (cache + favorites)
// Source phrases are in English; target-language translations are cached per (text, source, target, provider).

export const CATEGORIES = [
  {
    id: "greetings",
    label: "Greetings",
    phrases: [
      "Hello!",
      "Thank you so much.",
      "Goodbye, have a nice day.",
      "Sorry, I don't speak the language very well.",
      "Do you speak English?",
    ],
  },
  {
    id: "restaurant",
    label: "Restaurant",
    phrases: [
      "A table for two, please.",
      "Could I see the menu?",
      "What do you recommend?",
      "I'll have this one, please.",
      "Is this spicy?",
      "I'm allergic to nuts.",
      "The check, please.",
      "Where is the restroom?",
      "Can I have water, no ice?",
      "Delicious, thank you!",
    ],
  },
  {
    id: "shopping",
    label: "Shopping",
    phrases: [
      "How much does this cost?",
      "Do you accept credit card?",
      "Is there a discount?",
      "Do you have this in a smaller size?",
      "Can I try it on?",
      "I'll take it.",
      "Just looking, thanks.",
    ],
  },
  {
    id: "directions",
    label: "Directions",
    phrases: [
      "Where is the nearest train station?",
      "How do I get to this address?",
      "Is it far from here?",
      "Can you show me on the map?",
      "Which way to the beach?",
      "Is this the right bus?",
    ],
  },
  {
    id: "help",
    label: "Help",
    phrases: [
      "I need help, please.",
      "Call the police.",
      "I lost my passport.",
      "I need a doctor.",
      "Where is the pharmacy?",
      "I don't feel well.",
    ],
  },
  {
    id: "airport",
    label: "Airport",
    phrases: [
      "Where is the check-in counter?",
      "Which gate is my flight?",
      "Is my flight on time?",
      "Where is the baggage claim?",
      "My luggage is missing.",
      "I have a connecting flight.",
      "Can I have a window seat, please?",
      "Where is the boarding gate?",
      "How long is the layover?",
      "Where can I find the customs line?",
      "I need to declare this.",
      "Where is the nearest restroom?",
      "Is there free Wi-Fi here?",
      "Where can I rent a car?",
      "Where is the taxi stand?",
    ],
  },
];

const CACHE_KEY = "voyago_translate_cache_v2";  // bumped from v1 to invalidate stale bad entries
const FAV_KEY = "voyago_favorites_v1";
const CUSTOM_KEY = "tiplingo_custom_phrases_v1";

// Non-Latin target languages → expected script ranges. When a saved
// translation supposedly targets one of these but the actual text is
// pure ASCII (or any Latin), the entry is treated as STALE (likely
// from before the backend translator hardening) and the UI silently
// re-fetches it from the API.
const NON_LATIN_SCRIPT_RANGES = {
  Japanese:           [[0x3040, 0x309F], [0x30A0, 0x30FF], [0x4E00, 0x9FFF]],
  "Mandarin Chinese": [[0x4E00, 0x9FFF], [0x3400, 0x4DBF]],
  Chinese:            [[0x4E00, 0x9FFF], [0x3400, 0x4DBF]],
  Korean:             [[0xAC00, 0xD7AF], [0x1100, 0x11FF]],
  Arabic:             [[0x0600, 0x06FF], [0x0750, 0x077F]],
  Russian:            [[0x0400, 0x04FF]],
  Hindi:              [[0x0900, 0x097F]],
  Thai:               [[0x0E00, 0x0E7F]],
  Greek:              [[0x0370, 0x03FF]],
  Hebrew:             [[0x0590, 0x05FF]],
};

/**
 * Returns true iff `translation` looks valid for `targetLang`.
 * - For Latin-script targets (Spanish/French/Italian/German/Portuguese/English…)
 *   we only verify it isn't an exact echo of the source (after case+punct
 *   normalisation).
 * - For non-Latin targets we additionally require at least one character
 *   in the target's script range — catches the "LLM echoed English" bug.
 */
export function isValidTranslation(translation, source, targetLang) {
  if (!translation) return false;
  const ranges = NON_LATIN_SCRIPT_RANGES[targetLang];
  if (ranges) {
    let has = false;
    for (const ch of translation) {
      const cp = ch.codePointAt(0);
      for (const [lo, hi] of ranges) {
        if (cp >= lo && cp <= hi) { has = true; break; }
      }
      if (has) break;
    }
    if (!has) return false;
  }
  // Echo check (case + punctuation insensitive)
  const norm = (s) => (s || "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
  if (source && norm(translation) === norm(source)) return false;
  return true;
}

function safeRead(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function safeWrite(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    // Storage quota exceeded, disabled, or restricted private mode — non-fatal.
    if (process.env.NODE_ENV !== "production") {
      console.warn(`localStorage write failed for ${key}:`, err?.message || err);
    }
  }
}

function cacheId(text, source, target, provider) {
  return `${provider}|${source}|${target}|${text.trim().toLowerCase()}`;
}

// Cached audio is tagged with the OpenAI voice ID that rendered it
// (`audio_voice` field). Callers can pass `currentVoice` to getCached;
// if it doesn't match the cached `audio_voice`, audio_base64 is
// stripped from the returned entry so the caller refetches a fresh
// render in the user's CURRENT gender. The translation text itself is
// always safe to reuse — only the audio depends on gender.
export function getCached(text, source, target, provider, currentVoice) {
  const cache = safeRead(CACHE_KEY, {});
  const hit = cache[cacheId(text, source, target, provider)];
  if (!hit) return null;
  if (currentVoice && hit.audio_voice && hit.audio_voice !== currentVoice) {
    return { ...hit, audio_base64: null };
  }
  return hit;
}

export function setCached(text, source, target, provider, entry) {
  const cache = safeRead(CACHE_KEY, {});
  cache[cacheId(text, source, target, provider)] = {
    translation: entry.translation,
    audio_base64: entry.audio_base64 || null,
    audio_voice: entry.audio_base64 ? (entry.audio_voice || null) : null,
    created_at: Date.now(),
  };
  safeWrite(CACHE_KEY, cache);
}

// Strip every cached audio_base64 + audio_voice from the entire phrasebook
// cache. Used by TranslatePanel when the user toggles voice gender so the
// next replay of any phrase fetches a fresh audio clip in the new voice.
// Translation text is preserved.
export function clearCachedAudio() {
  const cache = safeRead(CACHE_KEY, {});
  let changed = false;
  for (const key of Object.keys(cache)) {
    if (cache[key] && cache[key].audio_base64) {
      cache[key] = {
        ...cache[key],
        audio_base64: null,
        audio_voice: null,
      };
      changed = true;
    }
  }
  if (changed) safeWrite(CACHE_KEY, cache);
}

export function getFavorites() {
  const list = safeRead(FAV_KEY, []);
  // sort newest first
  return [...list].sort((a, b) => (b.created_at || 0) - (a.created_at || 0));
}

export function isFavorite(source, translation) {
  const favs = safeRead(FAV_KEY, []);
  return favs.some(
    (f) => f.source === source && f.translation === translation
  );
}

export function toggleFavorite(msg) {
  const favs = safeRead(FAV_KEY, []);
  const idx = favs.findIndex(
    (f) => f.source === msg.source && f.translation === msg.translation
  );
  if (idx >= 0) {
    favs.splice(idx, 1);
  } else {
    favs.push({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      source: msg.source,
      translation: msg.translation,
      src: msg.src,
      tgt: msg.tgt,
      created_at: Date.now(),
    });
  }
  safeWrite(FAV_KEY, favs);
  return favs;
}

export function removeFavorite(id) {
  const favs = safeRead(FAV_KEY, []).filter((f) => f.id !== id);
  safeWrite(FAV_KEY, favs);
  return favs;
}

// ----- Custom phrases per category -----
// Stored as { [categoryId]: [{id, text}, ...] }
export function getCustomPhrases(categoryId) {
  const all = safeRead(CUSTOM_KEY, {});
  return all[categoryId] || [];
}

export function addCustomPhrase(categoryId, text) {
  const trimmed = (text || "").trim();
  if (!trimmed) return getCustomPhrases(categoryId);
  const all = safeRead(CUSTOM_KEY, {});
  const list = all[categoryId] || [];
  // dedupe (case-insensitive)
  if (list.some((p) => p.text.toLowerCase() === trimmed.toLowerCase())) {
    return list;
  }
  list.push({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    text: trimmed,
  });
  all[categoryId] = list;
  safeWrite(CUSTOM_KEY, all);
  return list;
}

export function removeCustomPhrase(categoryId, phraseId) {
  const all = safeRead(CUSTOM_KEY, {});
  const list = (all[categoryId] || []).filter((p) => p.id !== phraseId);
  all[categoryId] = list;
  safeWrite(CUSTOM_KEY, all);
  return list;
}
